/**
 * useMe — TanStack Query hook for the federated user's profile.
 *
 * Consumed by both extension popup + web. Queries GET /auth/me;
 * the api decodes the `nu_assist_session` JWT cookie and returns the
 * combined `UserMe` payload (federation row + app_users row) or a
 * status code that drives the UI state below.
 *
 * The `GET /auth/me` call is routed through the shared session-enforcing
 * `authedFetch` (see `../auth/session`). That means an *expired access*
 * cookie no longer drops the user to `no-user`: `authedFetch`
 * transparently `POST /auth/refresh`es (single-flight) and retries once,
 * so an active session silently recovers. Only a *hard* failure (refresh
 * cookie absent/invalid/expired) is terminal — `authedFetch` rejects
 * with `SessionExpiredError`, the injected `onSessionExpired` runs the
 * surface's lockout (web → clear cache + /login; extension → logged-out
 * view), and `useMe` reports `no-user` so the existing AuthGuard / popup
 * routing also lands on the logged-out UI.
 *
 * Status mapping (post-refresh-lifecycle, Phase 3):
 *   - 200 → `authenticated`
 *   - hard 401 (refresh also failed) → `no-user` (+ `onSessionExpired`)
 *   - 403 → `no-user` (cookie minted before Thrive deprovisioning;
 *           technically valid so refresh would "succeed" — still no user)
 *   - legacy 404 → `no-user` (pre-PR #56 compatibility)
 *   - 5xx / network / CORS → `error` (surfaced via TanStack Query)
 */
import { useQuery } from "@tanstack/react-query";
import type { UserMe } from "@nu-assist/contracts";
import { SessionExpiredError, makeAuthedFetch } from "../auth/session";

export interface UseMeOptions {
  /** API base URL. Default: window.location.origin's API counterpart. */
  apiBaseUrl?: string;
  /** Stale-time in ms. Default: 30s. */
  staleTime?: number;
  /**
   * Called once when the session is unrecoverable (access expired AND
   * refresh absent/invalid/expired). The surface injects its own
   * lockout: web clears the query cache + navigates to /login; the
   * extension popup flips to the logged-out view. Optional — when
   * omitted, `useMe` still reports `no-user` so existing AuthGuard /
   * popup routing handles the redirect on its own.
   */
  onSessionExpired?: () => void;
}

/**
 * Internal TanStack Query payload — what queryFn resolves to.
 */
type MeQueryPayload =
  | { data: UserMe; status: "authenticated" }
  | { data: null; status: "no-user" };

/**
 * Discriminated union exposed to consumers. Each branch's `data` is
 * correctly narrowed by TS — so `result.status === "authenticated"`
 * implies `result.data: UserMe` (non-null).
 */
export type UseMeResult =
  | { status: "loading"; data: null; error: undefined }
  | { status: "error"; data: null; error: Error }
  | { status: "no-user"; data: null; error: undefined }
  | { status: "authenticated"; data: UserMe; error: undefined };

// Canonical local dev URL — routed by Caddy + /etc/hosts to the API
// container (port 8000 internally, proxied through Caddy on :443).
// Last-resort fallback only; the consumer's Vite build replaces
// `import.meta.env.VITE_API_URL` at build time so the dev / prod
// bundles never reach this string.
const DEFAULT_API_BASE = "https://api.local.nu-assist.com";

/**
 * Resolve the API base URL.
 *
 * Precedence (highest first):
 *   1. Explicit `opts.apiBaseUrl` from the caller (defensive — let any
 *      consumer pin a different base for a single hook instance).
 *   2. `import.meta.env.VITE_API_URL` — set per stage by each app's
 *      `vite.config.ts` `define` block. Web + extension both bake their
 *      stage-resolved URL here at build time, so a `dev` bundle reads
 *      `https://api.dev.nu-assist.com` and a `prod` bundle reads
 *      `https://api.prod.nu-assist.com` automatically — without the
 *      consumer plumbing `apiBaseUrl` through every `useMe()` call site.
 *   3. `DEFAULT_API_BASE` — local dev safety net.
 *
 * This default-resolution path is what previously fell through to
 * `api.local.nu-assist.com` from dev/prod bundles whenever a consumer
 * forgot to pass `apiBaseUrl` — surfaced 2026-06-11 when the deployed
 * web at `app.dev.nu-assist.com` redirected to `oidc.local.nu-assist.com`
 * (Dex) instead of Google. Library-level default fixes ALL consumers
 * (5 call sites in web + 2 in extension popup) at once.
 */
function resolveApiBase(opts: UseMeOptions): string {
  if (opts.apiBaseUrl) return opts.apiBaseUrl;
  const fromEnv = import.meta.env?.VITE_API_URL;
  if (typeof fromEnv === "string" && fromEnv.length > 0) return fromEnv;
  return DEFAULT_API_BASE;
}

/**
 * Returns the current user profile from /auth/me. The request goes
 * through the shared session-enforcing `authedFetch`: an expired access
 * cookie is recovered transparently (refresh + retry); a hard expiry is
 * terminal (`onSessionExpired` fires, result is `no-user`).
 */
export function useMe(opts: UseMeOptions = {}): UseMeResult {
  const apiBase = resolveApiBase(opts);
  const url = `${apiBase}/auth/me`;

  const query = useQuery<MeQueryPayload>({
    queryKey: ["auth", "me", apiBase],
    staleTime: opts.staleTime ?? 30_000,
    // A failed refresh is a real auth state, not a transient fault —
    // retrying would just hammer a dead /auth/refresh, so the
    // SessionExpiredError → no-user mapping below is final for this
    // render. (Network/5xx still get TanStack Query's default retry
    // via the queryClient config.)
    retry: false,
    queryFn: async (): Promise<MeQueryPayload> => {
      // Single shared session implementation. `authedFetch` already
      // sends `credentials: "include"`; on a hard 401 it single-flight
      // `POST /auth/refresh`es, retries once, and on refresh failure
      // calls `onSessionExpired` then throws `SessionExpiredError`.
      const authedFetch = makeAuthedFetch({
        apiBase,
        onSessionExpired: opts.onSessionExpired ?? (() => {}),
      });

      let response: Response;
      try {
        response = await authedFetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
      } catch (err) {
        if (err instanceof SessionExpiredError) {
          // Hard expiry: refresh cookie absent/invalid/expired.
          // `onSessionExpired` already ran inside authedFetch; report
          // `no-user` so AuthGuard / the popup also land on the
          // logged-out UI even when no callback was injected.
          return { data: null, status: "no-user" };
        }
        throw err; // network/CORS — let TanStack Query render isError
      }

      // 403 (cookie minted before Thrive deprovisioning — technically
      // valid, so refresh would "succeed" but there is no user) and the
      // legacy 404 are also "no valid session". A retried 401 here
      // means the refresh succeeded but /auth/me still rejects — treat
      // it the same (no user).
      if (
        response.status === 401 ||
        response.status === 403 ||
        response.status === 404
      ) {
        return { data: null, status: "no-user" };
      }

      if (!response.ok) {
        // 5xx, CORS, network — let TanStack Query render it as isError
        throw new Error(
          `useMe: ${url} returned ${response.status} ${response.statusText}`,
        );
      }

      const user = (await response.json()) as UserMe;
      return { data: user, status: "authenticated" };
    },
  });

  // Normalize TanStack Query's internal state to UseMeResult. Each branch
  // returns the exact shape declared by the discriminated union so TS
  // narrows .data correctly in consumers.
  if (query.isLoading) {
    return { status: "loading", data: null, error: undefined };
  }
  if (query.isError) {
    return {
      status: "error",
      data: null,
      error: query.error instanceof Error ? query.error : new Error(String(query.error)),
    };
  }
  // Success — must have data (TanStack Query guarantees this when not
  // loading and not error). The cast is OK because TanStack's typing
  // doesn't model "exact-one-of {data, error}".
  if (!query.data) {
    return {
      status: "error",
      data: null,
      error: new Error("useMe: unexpected empty query result"),
    };
  }
  if (query.data.status === "no-user") {
    return { status: "no-user", data: null, error: undefined };
  }
  return { status: "authenticated", data: query.data.data, error: undefined };
}
